// Frontend/src/api/issues.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "./client";
import {
  CreateIssueCategoryData,
  CreateIssueData,
  Issue,
  IssueCategory,
  IssueComment,
  IssueFilters,
  IssueListItem,
  IssueStats,
  IssueTargetUnit,
  Paginated,
  UpdateIssueData,
} from "../types/issue";

const BASE = "/building/issues/";

/** Only send filters the server acts on; "ALL" and "" mean "do not filter". */
function toParams(filters: IssueFilters = {}): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;
    const v = String(value).trim();
    if (!v || v === "ALL") continue;
    params[key] = v;
  }
  return params;
}

/**
 * One page of issues. This endpoint is the only paginated one in the project,
 * so it returns an envelope rather than a bare array.
 */
export async function listIssues(
  filters: IssueFilters = {}
): Promise<Paginated<IssueListItem>> {
  const res = await api.get<Paginated<IssueListItem>>(BASE, { params: toParams(filters) });
  return res.data;
}

export async function listIssueCategories(buildingId?: string): Promise<IssueCategory[]> {
  const res = await api.get<IssueCategory[]>(`${BASE}categories/`, {
    params: buildingId ? { building: buildingId } : undefined,
  });
  return res.data;
}

export async function createIssueCategory(
  data: CreateIssueCategoryData,
  buildingId?: string
): Promise<IssueCategory> {
  const res = await api.post<IssueCategory>(`${BASE}categories/`, data, {
    params: buildingId ? { building: buildingId } : undefined,
  });
  return res.data;
}

export async function updateIssueCategory(
  id: string,
  data: Partial<Pick<IssueCategory, "name" | "icon" | "is_active" | "sort_order">>
): Promise<IssueCategory> {
  const res = await api.patch<IssueCategory>(`${BASE}categories/${id}/`, data);
  return res.data;
}

/**
 * The units of one building, each with the people who live there.
 *
 * Admins only — a resident never chooses a target; theirs is their own unit
 * or the common area.
 */
export async function listIssueTargets(buildingId?: string): Promise<IssueTargetUnit[]> {
  const res = await api.get<IssueTargetUnit[]>(`${BASE}targets/`, {
    params: buildingId ? { building: buildingId } : undefined,
  });
  return res.data;
}

export async function getIssue(id: string): Promise<Issue> {
  // A notification URL is untrusted input. Keep it in one path segment;
  // permissions still come from the scoped detail endpoint on the server.
  const res = await api.get<Issue>(`${BASE}${encodeURIComponent(id)}/`);
  return res.data;
}

/**
 * Create an issue. Always multipart: `attachments` is a repeated field, and
 * sending JSON when there are no files would mean two code paths on the
 * server for one operation.
 */
export async function createIssue(data: CreateIssueData): Promise<Issue> {
  const form = new FormData();
  form.append("title", data.title);
  form.append("description", data.description);
  if (data.category) form.append("category", data.category);
  if (data.new_category_name) form.append("new_category_name", data.new_category_name);
  if (data.new_category_icon) form.append("new_category_icon", data.new_category_icon);
  form.append("priority", data.priority);
  if (data.unit) form.append("unit", data.unit);
  if (data.building) form.append("building", data.building);
  for (const file of data.attachments ?? []) form.append("attachments", file);

  const res = await api.post<Issue>(BASE, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function updateIssue(id: string, data: UpdateIssueData): Promise<Issue> {
  const res = await api.patch<Issue>(`${BASE}${id}/`, data);
  return res.data;
}

export async function deleteIssue(id: string): Promise<void> {
  await api.delete(`${BASE}${id}/`);
}

export async function addIssueAttachment(id: string, file: File): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  await api.post(`${BASE}${id}/attachments/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function addIssueComment(
  id: string,
  body: string,
  isInternal = false
): Promise<IssueComment> {
  const res = await api.post<IssueComment>(`${BASE}${id}/comments/`, {
    body,
    is_internal: isInternal,
  });
  return res.data;
}

export async function getIssueStats(filters: IssueFilters = {}): Promise<IssueStats> {
  const res = await api.get<IssueStats>(`${BASE}stats/`, { params: toParams(filters) });
  return res.data;
}

// ==================== React Query Hooks ====================

export const ISSUES_QUERY_KEY = "issues";

/**
 * The filters are part of the key. Left out, React Query serves the previous
 * filter's rows while the new request is in flight, so switching tabs shows
 * the wrong list for a beat and then corrects itself.
 */
export function useIssues(
  filters: IssueFilters = {},
  options: { refetchInterval?: number | false; refetchOnWindowFocus?: boolean } = {}
) {
  return useQuery({
    queryKey: [ISSUES_QUERY_KEY, "list", filters],
    queryFn: () => listIssues(filters),
    // Without this the list blanks to a skeleton on every page change, which
    // makes paging feel like a full reload. Keeping the previous page visible
    // while the next one loads is the whole point of a pager.
    placeholderData: (previous) => previous,
    ...options,
  });
}

export const ISSUE_CATEGORIES_QUERY_KEY = "issue-categories";

export function useIssueCategories(buildingId?: string) {
  return useQuery({
    queryKey: [ISSUE_CATEGORIES_QUERY_KEY, buildingId ?? "mine"],
    queryFn: () => listIssueCategories(buildingId),
    // The building's category list changes when an admin edits it, not on
    // its own. Refetching it on every window focus is noise.
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateIssueCategory(buildingId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIssueCategoryData) => createIssueCategory(data, buildingId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ISSUE_CATEGORIES_QUERY_KEY] }),
  });
}

export function useUpdateIssueCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Pick<IssueCategory, "name" | "icon" | "is_active" | "sort_order">>;
    }) => updateIssueCategory(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ISSUE_CATEGORIES_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [ISSUES_QUERY_KEY] });
    },
  });
}

export const ISSUE_TARGETS_QUERY_KEY = "issue-targets";

/**
 * Keyed by building: an admin switching buildings mid-form must not see the
 * previous building's units, and without the id in the key React Query would
 * serve exactly that from cache.
 *
 * `enabled` because the picker cannot ask until a building is chosen.
 */
export function useIssueTargets(buildingId?: string, enabled = true) {
  return useQuery({
    queryKey: [ISSUE_TARGETS_QUERY_KEY, buildingId ?? "mine"],
    queryFn: () => listIssueTargets(buildingId),
    enabled,
    // Units and their occupants change when an admin edits them, not on
    // their own. Refetching on every focus while a form is open is noise.
    staleTime: 5 * 60 * 1000,
  });
}

export function useIssue(id: string | null | undefined) {
  return useQuery({
    queryKey: [ISSUES_QUERY_KEY, "detail", id],
    queryFn: () => getIssue(id as string),
    enabled: !!id,
  });
}

export function useIssueStats(filters: IssueFilters = {}) {
  return useQuery({
    queryKey: [ISSUES_QUERY_KEY, "stats", filters],
    queryFn: () => getIssueStats(filters),
  });
}

export function useCreateIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createIssue,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ISSUES_QUERY_KEY] });
      // A report may have created a category on its way through.
      qc.invalidateQueries({ queryKey: [ISSUE_CATEGORIES_QUERY_KEY] });
    },
  });
}

export function useUpdateIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIssueData }) => updateIssue(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ISSUES_QUERY_KEY] }),
  });
}

export function useDeleteIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteIssue,
    onSuccess: () => qc.invalidateQueries({ queryKey: [ISSUES_QUERY_KEY] }),
  });
}

export function useAddIssueAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => addIssueAttachment(id, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ISSUES_QUERY_KEY] }),
  });
}

export function useAddIssueComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, isInternal }: { id: string; body: string; isInternal?: boolean }) =>
      addIssueComment(id, body, isInternal),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ISSUES_QUERY_KEY] }),
  });
}
